import React, { useCallback, useState } from 'react';

import Link from '@/components/Link';
import { money } from '@/core/money';

import { ProjectERList } from './ProjectERList';
import { safeNum } from './constants';
import { dashboardCurrency } from './useDashboardData';
import type { DashboardContext, DashboardER, DashboardProject } from './useDashboardData';

interface ProjectRowProps {
  project: DashboardProject;
  /** Cost incurred on this project, in display cents per dashCtx. */
  costCents: number;
  dashCtx: DashboardContext;
  defaultExpanded?: boolean;
}

function computeProjectStats(ers: DashboardER[]) {
  let totalExperts = 0;
  let totalCompleted = 0;
  let totalScheduled = 0;
  let hasVerified = false;

  for (const er of ers) {
    totalExperts +=
      safeNum(er.stats?.suggested) +
      safeNum(er.stats?.vetting) +
      safeNum(er.stats?.verified) +
      safeNum(er.stats?.matched);
    totalCompleted += safeNum(er.stats?.completedCalls ?? er.stats?.calls);
    totalScheduled += safeNum(er.stats?.scheduledCalls);
    if (safeNum(er.stats?.verified) > 0) hasVerified = true;
  }

  return { requestCount: ers.length, totalExperts, totalCompleted, totalScheduled, hasVerified };
}

function StatIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      className="h-[1.3rem] w-[1.3rem] shrink-0 opacity-60"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export function ProjectRow({
  project,
  costCents,
  dashCtx,
  defaultExpanded = false,
}: ProjectRowProps): React.ReactNode {
  const currency = dashboardCurrency(dashCtx);
  const zeroLabel = currency === 'USD' ? '$0' : '0 credits';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  const ers = (project.expert_requests ?? []).filter(Boolean) as DashboardER[];
  const { requestCount, totalExperts, totalCompleted, totalScheduled, hasVerified } =
    computeProjectStats(ers);
  const isOnHold = ers.length > 0 && ers.every((er) => er.state === 'closed');

  return (
    <div className="last:border-b-0 border-b border-surface-border">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={`project-panel-${project.id}`}
        className="flex w-full cursor-pointer flex-wrap items-center gap-x-16 gap-y-8 border-none bg-transparent px-20 py-16 text-left transition-colors hover:bg-surface-elevated"
      >
        <svg
          className={`h-[2rem] w-[2rem] shrink-0 text-on-surface-muted transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>

        <div className="min-w-0 flex-1 max-lg:basis-full">
          <Link
            to={project.html_url ?? '#'}
            className="text-14 font-semibold text-brand-primary no-underline hover:underline"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {project.name}
          </Link>
        </div>

        <span
          className={`inline-flex items-center rounded-full px-8 py-2 text-[1.1rem] font-medium ${
            isOnHold
              ? 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-300'
              : 'bg-green-500/15 text-green-700 dark:text-green-300'
          }`}
        >
          {isOnHold ? 'On Hold' : 'Active'}
        </span>

        <div className="flex flex-wrap items-center gap-x-16 gap-y-4 text-12 text-on-surface-muted">
          <span className="flex items-center gap-4 whitespace-nowrap">
            <StatIcon>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </StatIcon>
            <strong className="font-semibold text-on-surface">{requestCount}</strong>{' '}
            {requestCount === 1 ? 'request' : 'requests'}
          </span>
          <span className="flex items-center gap-4 whitespace-nowrap">
            <StatIcon>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </StatIcon>
            <strong className="font-semibold text-on-surface">{totalExperts}</strong>{' '}
            {totalExperts === 1 ? 'expert' : 'experts'}
          </span>
          <span className="flex items-center gap-4 whitespace-nowrap">
            <StatIcon>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </StatIcon>
            <strong className="font-semibold text-on-surface">{totalCompleted}</strong> completed
          </span>
          {totalScheduled > 0 && (
            <span className="flex items-center gap-4 whitespace-nowrap">
              <StatIcon>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </StatIcon>
              <strong className="font-semibold text-on-surface">{totalScheduled}</strong> scheduled
            </span>
          )}
        </div>

        {hasVerified && (
          <span
            className="h-[0.8rem] w-[0.8rem] shrink-0 rounded-full bg-yellow-500"
            aria-label="Needs attention"
          />
        )}

        <span
          className={`min-w-[9rem] text-right text-[1.3rem] font-semibold tabular-nums ${costCents > 0 ? 'text-brand-primary' : 'text-on-surface-subtle'}`}
        >
          {costCents > 0
            ? money(costCents, currency).formatted({ omitEmptyCents: true })
            : zeroLabel}
        </span>
      </button>

      <div
        id={`project-panel-${project.id}`}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="pt-0 bg-surface-elevated px-20 pb-16">
            <ProjectERList expertRequests={ers} expanded={expanded} />
          </div>
        </div>
      </div>
    </div>
  );
}
